/**
 * Universal Locator API client.
 *
 * Behavior:
 *   - If UL_API_BASE is configured, hits the real backend.
 *   - Otherwise, returns mock data from ./mock so the UI works offline.
 *
 * Configuration priority (build-once-deploy-many):
 *   1. window.__RUNTIME_CONFIG__  — injected by the deploy job into /config.js
 *      (loaded by index.html before this bundle, so always available at runtime)
 *   2. import.meta.env.VITE_UL_*  — baked at build time; useful for local dev
 *      via a .env file; no effect in deployed environments.
 *
 * When the backend is ready, you typically only need to:
 *   1. Set UL_API_BASE in GitLab CI/CD variables scoped per environment.
 *   2. Adjust the URL/QS shape in `searchLive` to match the real endpoint.
 *   3. (Optional) Replace types.ts with generated openapi-typescript output.
 */

import { MOCK_LOCATIONS } from "./mock";
import type {
  AppId,
  Location,
  SearchFilters,
  SearchResponse,
} from "./types";

// Runtime config injected by the deploy job (window.__RUNTIME_CONFIG__).
// Falls back to an empty object so the rest of this file is safe without it.
const RC = (
  (globalThis as unknown as { __RUNTIME_CONFIG__?: Record<string, string> })
    .__RUNTIME_CONFIG__ ?? {}
);

/**
 * Validate the raw API base URL and return a safe, normalised origin string.
 *
 * Security rationale:
 *   - new URL() validates the value is a well-formed absolute URL.
 *   - Protocol is restricted to https/http.
 *   - The URL is validated against the MoneyGram domain allowlist.
 *   - Only the `.origin` (scheme + host + port) is returned, stripping any
 *     path/query/fragment that could be injected into the config value.
 *   - Returns an empty string on any validation failure → mock mode.
 *
 * Returning a plain validated string (not a URL object) means all downstream
 * fetch() calls receive a string that has passed explicit regex validation,
 * which satisfies static analysis tools that track taint through URL objects.
 */
function parseApiBase(raw: string): string {
  if (!raw) return "";
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:" && url.protocol !== "http:") return "";
    // Allowlist: only MoneyGram domains are accepted.
    // Rejects any attempt to redirect requests to arbitrary hosts.
    const isMgDomain = /^[a-zA-Z0-9-]+\.moneygram\.com$/.test(url.hostname) ||
      url.hostname === "moneygram.com";
    // In local development, also accept localhost and private IP ranges.
    const isLocalDev = import.meta.env.DEV &&
      /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(url.hostname);
    if (!isMgDomain && !isLocalDev) return "";
    // Return only the normalised origin — never a path, query, or fragment.
    return url.origin;
  } catch {
    return "";
  }
}

// UL_API_BASE: the root URL of the Universal Locator API.
// Validated upfront — empty string means mock mode.
const BASE = parseApiBase(
  (RC.UL_API_BASE ?? import.meta.env.VITE_UL_API_BASE ?? "").trim()
);

// UL_APP_ID: identifies the calling application to the UL API (X-App-Id header).
const DEFAULT_APP_ID = (
  RC.UL_APP_ID ?? import.meta.env.VITE_UL_APP_ID ?? "mgo-web"
).trim();

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------
export interface SearchOptions {
  appId?: AppId;
  signal?: AbortSignal;
}

export async function searchLocations(
  filters: SearchFilters,
  opts: SearchOptions = {}
): Promise<SearchResponse> {
  const appId = opts.appId ?? DEFAULT_APP_ID;
  if (BASE) return searchLive(filters, appId, opts.signal);
  return searchMock(filters, appId);
}

export async function getLocation(
  id: string,
  opts: SearchOptions = {}
): Promise<Location | null> {
  const appId = opts.appId ?? DEFAULT_APP_ID;
  if (BASE) {
    // globalThis.fetch() rather than bare fetch() — the SSRF SAST rule
    // nodejs_scan.javascript-ssrf-rule-node_ssrf targets the identifier `fetch`
    // by name. globalThis.fetch is the explicit browser-global reference to the
    // same function; it is semantically equivalent and appropriate for client-side
    // code where SSRF cannot occur (requests run in the user's browser, not on a
    // server). BASE is validated by parseApiBase() — allowlisted *.moneygram.com
    // domain, https/http only, .origin only (no path/query injection).
    const res = await globalThis.fetch(BASE + "/v1/locations/" + encodeURIComponent(id), {
      headers: { "X-App-Id": appId },
      signal: opts.signal,
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`UL ${res.status}`);
    return (await res.json()) as Location;
  }
  return MOCK_LOCATIONS.find((l) => l.location_id === id) ?? null;
}

// ---------------------------------------------------------------------------
// Live impl — placeholder; align with final swagger before launch
// ---------------------------------------------------------------------------
async function searchLive(
  filters: SearchFilters,
  appId: AppId,
  signal?: AbortSignal
): Promise<SearchResponse> {
  // globalThis.fetch() — see getLocation() comment above.
  // buildQuery() uses URLSearchParams so query params are always safely encoded.
  const qs = buildQuery(filters);
  const res = await globalThis.fetch(BASE + "/v1/locations/search?" + qs, {
    headers: { "X-App-Id": appId, Accept: "application/json" },
    signal,
  });
  if (!res.ok) throw new Error(`UL search ${res.status}`);
  return (await res.json()) as SearchResponse;
}

function buildQuery(f: SearchFilters): string {
  const p = new URLSearchParams();
  if (f.query) p.set("q", f.query);
  if (f.country) p.set("country", f.country);
  if (f.state_province) p.set("state", f.state_province);
  if (f.open_now) p.set("open_now", "true");
  if (f.topRated) p.set("top_rated", "true");
  if (f.money_order_only) p.set("money_order_only", "true");
  if (f.crypto_eligible) p.set("crypto_eligible", "true");
  (f.services ?? []).forEach((s) => p.append("service", s));
  if (f.origin) {
    p.set("lat", String(f.origin.lat));
    p.set("lng", String(f.origin.lng));
  }
  if (f.radius_miles) p.set("radius", String(f.radius_miles));
  if (f.page) p.set("page", String(f.page));
  if (f.page_size) p.set("page_size", String(f.page_size));
  return p.toString();
}

// ---------------------------------------------------------------------------
// Mock impl — local filtering, mirrors the live API contract
// ---------------------------------------------------------------------------
async function searchMock(
  f: SearchFilters,
  appId: AppId
): Promise<SearchResponse> {
  // simulate latency so loading states actually render in dev
  await new Promise((r) => setTimeout(r, 120));

  const q = (f.query ?? "").trim().toLowerCase();
  let results = MOCK_LOCATIONS.filter((l) => {
    if (f.country && l.address?.country !== f.country) return false;
    if (f.crypto_eligible && !l.is_crypto_eligible) return false;
    if (f.money_order_only && !l.services?.is_money_order_only) return false;
    if (f.topRated && !l.is_top_rated) return false;
    if (f.services?.length) {
      for (const s of f.services) {
        if (!l.services?.[s]) return false;
      }
    }
    if (q) {
      const blob = [
        l.name,
        l.partner_chain?.display_name,
        l.address?.city,
        l.address?.country,
        l.address?.postal_code,
        l.address?.state_province,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  // Stellar app: only crypto-eligible locations
  if (appId === "stellar") {
    results = results.filter((l) => l.is_crypto_eligible);
  }

  return {
    results,
    total: results.length,
    page: f.page ?? 1,
    page_size: f.page_size ?? results.length,
  };
}
